import type {
	FieldBindingCatalogueRes,
	InvestorOption,
	PreviewRes,
} from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';
import type {
	DocumentFlowDoc,
	FlowRuleScenario,
} from '@dealstack/shared/lib/common/business/documentFlow';
import type { RuleCohort } from '@dealstack/frontend/shared/components/DocumentEditor/rules.types';
import type { SignatureSlotDisplay } from '@dealstack/frontend/shared/components/DocumentEditor/extensions/signatureNode';
import type { SignatureSlotListItem } from '@dealstack/frontend/shared/components/DocumentEditor/flowDocumentEditor.types';

/** The reflow document and what the editor derives from it, as the endpoint returns it. */
export interface DocxFlowDocumentData {
	document: unknown;
	fields: Array<{ name: string; bindingTemplate: string | null }>;
	problems: Array<{ kind: string; message: string }>;
	warnings: string[];
	unlinked: string[];
	showStampMetadata: boolean;
}

export interface DocxFlowDocumentQueryResult {
	data: DocxFlowDocumentData | undefined;
	isLoading: boolean;
	isError: boolean;
}

export interface DocxFlowSaveMutation {
	mutate: (
		args: { templateId: string; document: unknown; bindings?: Record<string, string> },
		options?: { onSuccess?: () => void }
	) => void;
	isPending: boolean;
	isError: boolean;
	error: unknown;
}

export interface DocxFlowRenderMutation {
	mutate: (args: {
		templateId: string;
		investorId?: string;
		document?: unknown;
		bindings?: Record<string, string>;
		scenario?: FlowRuleScenario;
	}) => void;
	data: PreviewRes | undefined;
	isPending: boolean;
}

export interface DocxCatalogueQueryResult {
	data: FieldBindingCatalogueRes | undefined;
	isPending: boolean;
}

/** An owner with no signing roles reports `isSignable: false`, which hides the tab. */
export interface DocxSignatureSlotsResult {
	isSignable: boolean;
	signatureSlotList: SignatureSlotListItem[];
	signatureSlotDisplay: Record<string, SignatureSlotDisplay>;
	unplacedCount: number;
	isPartiallyPlaced: boolean;
	duplicatedLabels: string[];
	headerRowLabels: string[];
	isSlotsUnknown: boolean;
	isComplete: boolean;
}

export interface DocxDateAnchorsResult {
	requiresPostDating: boolean;
	duplicatedLabels: string[];
	headerRowLabels: string[];
	isComplete: boolean;
}

/** `isAvailable: false` where there is no deal and so no investors to preview as. */
export interface DocxInvestorPreviewResult {
	isAvailable: boolean;
	options: InvestorOption[];
	onDropdownOpen: () => void;
	isFetching: boolean;
}

/**
 * Everything the Word editor needs that depends on WHO owns the template, so the editor itself
 * holds no route params and no notion of a deal.
 *
 * These are hooks, called in a fixed order every render, so an adapter must be a module-level
 * constant — one swapped mid-mount would change the hook order and tear React's state.
 */
export interface DocxFlowEditorAdapter {
	useFlowDocument: (templateId: string) => DocxFlowDocumentQueryResult;
	useSaveFlowDocument: () => DocxFlowSaveMutation;
	/** `investorId` is the preview subject; the global adapter ignores it. */
	useCatalogue: (templateId: string, investorId: string | null) => DocxCatalogueQueryResult;
	useRender: () => DocxFlowRenderMutation;
	useSignatureSlots: (
		templateId: string,
		document: DocumentFlowDoc | null
	) => DocxSignatureSlotsResult;
	useDateAnchors: (templateId: string, document: DocumentFlowDoc | null) => DocxDateAnchorsResult;
	useInvestorPreview: (options: { preload: boolean }) => DocxInvestorPreviewResult;
	/** Governed groupings the rule picker offers. Empty where there is no programme. */
	useCohorts: () => RuleCohort[];
}

/** What an owner that places no signatures reports. */
export const NO_SIGNATURE_SLOTS: DocxSignatureSlotsResult = {
	isSignable: false,
	signatureSlotList: [],
	signatureSlotDisplay: {},
	unplacedCount: 0,
	isPartiallyPlaced: false,
	duplicatedLabels: [],
	headerRowLabels: [],
	// Not "unknown": nothing to wait for, and unknown would hold the editor's loading gate open.
	isSlotsUnknown: false,
	isComplete: true,
};

export const NO_DATE_ANCHORS: DocxDateAnchorsResult = {
	requiresPostDating: false,
	duplicatedLabels: [],
	headerRowLabels: [],
	isComplete: true,
};

/** Stable identity matters: cohorts feed three memo dependency lists in the editor. */
export const NO_COHORTS: RuleCohort[] = [];

export const NO_INVESTOR_PREVIEW: DocxInvestorPreviewResult = {
	isAvailable: false,
	options: [],
	onDropdownOpen: () => undefined,
	isFetching: false,
};
